import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiService } from '@/services/api';
import {
  Account,
  AccountingPeriod,
  ReconciliationAccount,
  ReconciliationSummary,
  AuthStatus,
  DashboardStats,
  ModuleInfo,
} from '@/types';
import { useSubsidiary } from '@/contexts/SubsidiaryContext';
import { usePeriod } from '@/contexts/PeriodContext';

// Query keys for React Query
export const queryKeys = {
  health: ['health'],
  apiInfo: ['api-info'],
  authStatus: ['auth-status'],
  accounts: ['accounts'],
  accountingPeriods: ['accounting-periods'],
  currentPeriod: ['current-period'],
  subsidiaries: ['subsidiaries'],
  reconciliationAccounts: (subsidiary_id?: number) => ['reconciliation-accounts', subsidiary_id],
  reconciliationDetail: (id: number) => ['reconciliation-detail', id],
  reconciliationSummary: (subsidiary_id?: number) => ['reconciliation-summary', subsidiary_id],
  dashboardStats: (subsidiary_id?: number) => ['dashboard-stats', subsidiary_id],
  recentActivity: (roleView?: string) => ['recent-activity', roleView],
  workQueue: (roleView?: string) => ['work-queue', roleView],
  modules: ['modules'],
} as const;

// Health and Info hooks
export const useHealth = () => {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: apiService.getHealth,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useApiInfo = () => {
  return useQuery({
    queryKey: queryKeys.apiInfo,
    queryFn: apiService.getApiInfo,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Authentication hooks
export const useAuthStatus = () => {
  return useQuery<AuthStatus>({
    queryKey: queryKeys.authStatus,
    queryFn: async () => {
      try {
        return await apiService.getAuthStatus();
      } catch (error: any) {
        // 401 means not authenticated, which is a valid state, not an error
        if (error.response?.status === 401) {
          return { authenticated: false };
        }
        throw error;
      }
    },
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useModules = () => {
  return useQuery<{ enabled: ModuleInfo[]; access?: string[]; available?: string[]; new_user_defaults?: string[] }>({
    queryKey: queryKeys.modules,
    queryFn: () => apiService.getEnabledModules(),
    staleTime: 10 * 60 * 1000, // module config rarely changes
    retry: false,
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: apiService.loginWithOkta,
    onSuccess: () => {
      // Don't show success toast here as we'll redirect to Okta
      // Success will be handled after Okta callback
      queryClient.invalidateQueries({ queryKey: queryKeys.authStatus });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Login failed';
      toast.error(message);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: apiService.logout,
    onSuccess: () => {
      queryClient.clear();
      toast.success('Logged out successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Logout failed';
      toast.error(message);
    },
  });
};

// Account hooks
export const useAccounts = () => {
  return useQuery<Account[]>({
    queryKey: queryKeys.accounts,
    queryFn: apiService.getAccounts,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Period hooks
export const useAccountingPeriods = () => {
  return useQuery<AccountingPeriod[]>({
    queryKey: queryKeys.accountingPeriods,
    queryFn: apiService.getAccountingPeriods,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useCurrentPeriod = () => {
  return useQuery<AccountingPeriod | null>({
    queryKey: queryKeys.currentPeriod,
    queryFn: apiService.getCurrentPeriod,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Subsidiary hooks
export const useSubsidiaries = () => {
  return useQuery({
    queryKey: queryKeys.subsidiaries,
    queryFn: apiService.getSubsidiaries,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Reconciliation hooks
export const useReconciliationAccounts = (subsidiary_id?: number | null, periodId?: number | null, roleView?: string) => {
  console.log('useReconciliationAccounts - periodId:', periodId, 'subsidiaryId:', subsidiary_id, 'roleView:', roleView);
  
  return useQuery<ReconciliationAccount[]>({
    queryKey: ['reconciliationAccounts', subsidiary_id, periodId, roleView],
    queryFn: () => apiService.getReconciliationAccounts(subsidiary_id ?? undefined, periodId, roleView),
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
  });
};

export const useReconciliationPeriods = () => {
  return useQuery({
    queryKey: ['reconciliationPeriods'],
    queryFn: () => apiService.getReconciliationPeriods(),
  });
};

export const useReconciliationSummary = () => {
  const { selectedSubsidiary } = useSubsidiary();
  const { selectedPeriod } = usePeriod();
  const subsidiaryId = selectedSubsidiary?.id;
  const periodId = selectedPeriod?.id;
  
  return useQuery<ReconciliationSummary>({
    queryKey: ['reconciliationSummary', subsidiaryId, periodId],
    queryFn: () => apiService.getReconciliationSummary(subsidiaryId, periodId),
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
  });
};

export const useDashboardStats = (periodId?: number | null) => {
  const { selectedSubsidiary } = useSubsidiary();
  const subsidiaryId = selectedSubsidiary?.id;
  
  return useQuery<DashboardStats>({
    queryKey: ['dashboard-stats', subsidiaryId, periodId],
    queryFn: () => apiService.getDashboardStats(subsidiaryId, periodId),
    refetchInterval: 60 * 1000, // Refresh every minute
  });
};

// Reconciliation workflow mutations
export const useStartReconciliation = () => {
  const queryClient = useQueryClient();
  const { selectedSubsidiary } = useSubsidiary();
  const subsidiaryId = selectedSubsidiary?.id;
  
  return useMutation({
    mutationFn: ({ account_id, periodId }: { account_id?: number; periodId: number }) =>
      apiService.startReconciliation(account_id, periodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationAccounts(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationSummary(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats(subsidiaryId) });
      toast.success('Reconciliation started successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to start reconciliation';
      toast.error(message);
    },
  });
};

export const useSubmitForApproval = () => {
  const queryClient = useQueryClient();
  const { selectedSubsidiary } = useSubsidiary();
  const subsidiaryId = selectedSubsidiary?.id;
  
  return useMutation({
    mutationFn: ({ account_id, periodId }: { account_id?: number; periodId: number }) =>
      apiService.submitForApproval(account_id, periodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationAccounts(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationSummary(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats(subsidiaryId) });
      toast.success('Submitted for approval successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to submit for approval';
      toast.error(message);
    },
  });
};

export const useSubmitReconciliation = () => {
  const queryClient = useQueryClient();
  const { selectedSubsidiary } = useSubsidiary();
  const subsidiaryId = selectedSubsidiary?.id;
  
  return useMutation({
    mutationFn: ({ reconciliationId }: { reconciliationId: number }) =>
      apiService.submitReconciliation(reconciliationId),
    onSuccess: (result: { required_warnings?: string[] } | undefined) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationAccounts(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationSummary(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats(subsidiaryId) });
      toast.success('Reconciliation submitted for approval successfully');
      // CORE-F16: submitted, but the template asks for more (refused instead where the organisation enforces it)
      const gaps = result?.required_warnings ?? [];
      if (gaps.length) toast(`The template asks for: ${gaps.join('; ')}. Your approver will see it's missing.`, { icon: '⚠️', duration: 8000 });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to submit reconciliation';
      toast.error(message);
    },
  });
};

export const useApproveReconciliation = () => {
  const queryClient = useQueryClient();
  const { selectedSubsidiary } = useSubsidiary();
  const subsidiaryId = selectedSubsidiary?.id;
  
  return useMutation({
    mutationFn: ({ reconciliationId, comments, confirmBalanceChange }:
                 { reconciliationId: number; comments?: string; confirmBalanceChange?: boolean }) =>
      apiService.approveReconciliation(reconciliationId, comments, confirmBalanceChange),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationAccounts(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationSummary(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats(subsidiaryId) });
      toast.success('Reconciliation approved successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to approve reconciliation';
      toast.error(message);
    },
  });
};

export const useRejectReconciliation = () => {
  const queryClient = useQueryClient();
  const { selectedSubsidiary } = useSubsidiary();
  const subsidiaryId = selectedSubsidiary?.id;
  
  return useMutation({
    mutationFn: ({ reconciliationId, reason }: { reconciliationId: number; reason: string }) =>
      apiService.rejectReconciliation(reconciliationId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationAccounts(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationSummary(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats(subsidiaryId) });
      toast.success('Reconciliation rejected');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to reject reconciliation';
      toast.error(message);
    },
  });
};

export const useReopenReconciliation = () => {
  const queryClient = useQueryClient();
  const { selectedSubsidiary } = useSubsidiary();
  const subsidiaryId = selectedSubsidiary?.id;
  
  return useMutation({
    mutationFn: ({ reconciliationId, reason }: { reconciliationId: number; reason: string }) =>
      apiService.reopenReconciliation(reconciliationId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationAccounts(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationSummary(subsidiaryId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats(subsidiaryId) });
      toast.success('Reconciliation reopened successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || 'Failed to reopen reconciliation';
      toast.error(message);
    },
  });
};

// Dashboard activity hooks
export const useRecentActivity = (roleView?: string, periodId?: number | null) => {
  const { selectedSubsidiary } = useSubsidiary();
  const subsidiaryId = selectedSubsidiary?.id;
  
  return useQuery({
    queryKey: ['recent-activity', roleView, subsidiaryId, periodId],
    queryFn: () => apiService.getRecentActivity(roleView, 10, subsidiaryId, periodId),
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
  });
};

export const useWorkQueue = (roleView?: string, periodId?: number | null) => {
  const { selectedSubsidiary } = useSubsidiary();
  const subsidiaryId = selectedSubsidiary?.id;
  
  return useQuery({
    queryKey: ['work-queue', roleView, subsidiaryId, periodId],
    queryFn: () => apiService.getWorkQueue(roleView, 10, subsidiaryId, periodId),
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
  });
};

// Reconciliation detail hooks
export const useReconciliationDetail = (reconciliationId: number) => {
  return useQuery({
    queryKey: queryKeys.reconciliationDetail(reconciliationId),
    queryFn: () => apiService.getReconciliationDetail(reconciliationId),
    enabled: !!reconciliationId && reconciliationId > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
    retry: 1,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useAddReconciliationItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ reconciliationId, data }: { reconciliationId: number; data: any }) =>
      apiService.addReconciliationItem(reconciliationId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationDetail(variables.reconciliationId) });
      toast.success('Item added successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to add item';
      toast.error(message);
    },
  });
};

export const useUpdateReconciliationItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ reconciliationId, itemId, data }: { reconciliationId: number; itemId: number; data: any }) =>
      apiService.updateReconciliationItem(reconciliationId, itemId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationDetail(variables.reconciliationId) });
      toast.success('Item updated successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to update item';
      toast.error(message);
    },
  });
};

export const useDeleteReconciliationItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ reconciliationId, itemId }: { reconciliationId: number; itemId: number }) =>
      apiService.deleteReconciliationItem(reconciliationId, itemId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationDetail(variables.reconciliationId) });
      toast.success('Item deleted successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to delete item';
      toast.error(message);
    },
  });
};

export const useAddComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ reconciliationId, comment }: { reconciliationId: number; comment: string }) =>
      apiService.addReconciliationComment(reconciliationId, comment),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.reconciliationDetail(variables.reconciliationId) });
      toast.success('Comment added successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to add comment';
      toast.error(message);
    },
  });
};